import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { complianceAPI } from '../lib/api';

const inputStyle = {
  width: '100%', padding: '0.75rem', borderRadius: 10, border: '1px solid #CBD5E1',
  background: '#FAF7F2', color: '#002A38', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748B', marginBottom: '0.375rem' };

const COPY = {
  ada_accommodation: {
    title: 'Request a Disability Accommodation',
    blurb: 'Under the Americans with Disabilities Act (ADA), you may request a reasonable accommodation or an alternative way to complete this step (e.g., extended time, a different format, or assistive technology). Tell us what would help and we will respond promptly. This request is confidential and will not negatively affect your candidacy.',
    placeholder: 'Describe the accommodation you need (e.g., extra time, screen-reader-friendly format, breaks)…',
    cta: 'Submit Accommodation Request',
  },
  aedt_alternative: {
    title: 'Request an Alternative Process',
    blurb: 'This employer uses an Automated Employment Decision Tool (AEDT) to help evaluate candidates, as disclosed under New York City Local Law 144. You may request an alternative selection process or accommodation. Tell us what you would prefer and we will respond promptly. This request will not negatively affect your candidacy.',
    placeholder: 'Tell us what alternative process or accommodation you would like…',
    cta: 'Submit Alternative-Process Request',
  },
};

export default function ComplianceRequestDialog({
  open, onOpenChange, category, context, accessCode, jobTitle,
  defaultName = '', defaultEmail = '',
}) {
  const copy = COPY[category] || COPY.ada_accommodation;
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (open) {
      setName(defaultName);
      setEmail(defaultEmail);
      setDetails('');
      setDone(null);
    }
  }, [open, defaultName, defaultEmail]);

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error('Please enter your name and email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await complianceAPI.submitCandidateRequest({
        category, name: name.trim(), email: email.trim(),
        context: context || null, access_code: accessCode || null,
        job_title: jobTitle || null, details: details.trim() || null,
      });
      setDone(res.data.id);
      toast.success('Your request has been submitted.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, maxWidth: 480 }} data-testid="compliance-request-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Cabinet Grotesk', color: '#0076A3' }}>{copy.title}</DialogTitle>
          <DialogDescription style={{ fontSize: '0.8125rem', color: '#64748B', lineHeight: 1.7 }}>{copy.blurb}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div data-testid="compliance-request-success" style={{ padding: '1.5rem', borderRadius: 12, border: '1px solid #BBF7D0', background: '#F0FDF4' }}>
            <p style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#166534', marginBottom: '0.375rem' }}>Request received</p>
            <p style={{ fontSize: '0.8125rem', color: '#15803D', lineHeight: 1.6, margin: 0 }}>
              Thank you. Your reference number is <strong>{done}</strong>. We've emailed a confirmation to <strong>{email}</strong> and our team has been notified.
            </p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={labelStyle}>Full name</label>
                <input data-testid="compliance-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input data-testid="compliance-email" type="email" style={inputStyle} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              </div>
              <div>
                <label style={labelStyle}>What would help? (optional)</label>
                <textarea data-testid="compliance-details" style={{ ...inputStyle, minHeight: 96, resize: 'vertical' }} value={details} onChange={(e) => setDetails(e.target.value)} placeholder={copy.placeholder} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={() => onOpenChange(false)} style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }} data-testid="compliance-close">Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} style={{ borderRadius: 10 }}>Cancel</Button>
              <Button data-testid="compliance-submit" onClick={submit} disabled={submitting} style={{ background: submitting ? '#94A3B8' : '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
                {submitting ? 'Submitting…' : copy.cta}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
